import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PriceHistoryComponent } from '../../../../../shared/components/price-history/price-history.component';
import { Price } from '../../../../../shared/models/catalog/price.model';
import {
  StoreTicketSchema,
  Ticket,
} from '../../../../../shared/models/catalog/ticket.model';
import { TicketService } from '../../../../../shared/services/catalog/ticket.service';
import {
  DEFAULT_LANGUAGE_CODE,
  LANGUAGE_CODE_LABELS,
  LANGUAGE_CODES,
  LanguageCode,
} from '../../../../../shared/util/language-codes';
import { applyZodErrors } from '../../../../../shared/util/zod-form-errors';

interface PersistedTicketDetail {
  language: string;
  title: string;
}

interface LanguageOption {
  label: string;
  value: LanguageCode;
}

@Component({
  selector: 'app-ticket-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    PanelModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    ToggleSwitchModule,
    TagModule,
    ToastModule,
    ConfirmDialogModule,
    PriceHistoryComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './ticket-panel.component.html',
  styleUrl: './ticket-panel.component.scss',
})
export class TicketPanelComponent implements OnInit {
  #service = inject(TicketService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #fb = inject(FormBuilder);

  ticket = input<Ticket | null>(null);
  productId = input.required<number>();

  saved = output<Ticket>();
  deleted = output<number>();

  current = signal<Ticket | null>(null);
  submitting = signal(false);

  detailsRevision = signal(0);

  defaultLanguage = DEFAULT_LANGUAGE_CODE;

  prices = computed<Price[]>(() => {
    const t = this.current();
    return Array.isArray(t?.prices) ? (t.prices as Price[]) : [];
  });

  isPersisted = computed<boolean>(() => this.current() !== null);

  form: FormGroup = this.#fb.group({
    details: this.#fb.array<FormGroup>([]),
    min_amount: [1 as number | null],
    max_amount: [null as number | null],
    is_included: [true as boolean],
  });

  languagePickerControl = new FormControl<LanguageCode | null>(null);

  get details(): FormArray<FormGroup> {
    return this.form.get('details') as FormArray<FormGroup>;
  }

  usedLanguages = computed<Set<LanguageCode>>(() => {
    this.detailsRevision();
    const used = new Set<LanguageCode>();
    for (const ctrl of this.details.controls) {
      const lang = ctrl.get('language')?.value as LanguageCode | undefined;
      if (lang) used.add(lang);
    }
    return used;
  });

  availableLanguages = computed<LanguageOption[]>(() => {
    const used = this.usedLanguages();
    return LANGUAGE_CODES.filter((code) => !used.has(code)).map((code) => ({
      label: LANGUAGE_CODE_LABELS[code],
      value: code,
    }));
  });

  ngOnInit(): void {
    const initial = this.ticket();
    if (initial) {
      this.current.set(initial);
      this.#patchFromTicket(initial);
    } else {
      this.#resetDetailsTo([]);
    }
  }

  isDirty(): boolean {
    return !this.isPersisted() || this.form.dirty;
  }

  headerTitle(): string {
    const c = this.current();
    if (!c) return this.#translate.instant('catalog.tickets.add');
    const detail = (c.details as PersistedTicketDetail[])
      ?.find((d) => d.language === DEFAULT_LANGUAGE_CODE);
    return detail?.title ?? `Ticket #${c.id}`;
  }

  labelForLanguage(code: string): string {
    return LANGUAGE_CODE_LABELS[code as LanguageCode] ?? code;
  }

  hasPendingLanguage(): boolean {
    return this.languagePickerControl.value !== null;
  }

  addLanguageRow() {
    const lang = this.languagePickerControl.value;
    if (!lang) return;
    if (this.usedLanguages().has(lang)) return;
    this.details.push(this.#buildDetailGroup(lang));
    this.languagePickerControl.setValue(null);
    this.detailsRevision.update((n) => n + 1);
    this.form.markAsDirty();
  }

  removeLanguageRow(index: number) {
    const ctrl = this.details.at(index);
    if (!ctrl) return;
    if ((ctrl.get('language')?.value as LanguageCode) === DEFAULT_LANGUAGE_CODE) {
      return;
    }
    this.details.removeAt(index);
    this.detailsRevision.update((n) => n + 1);
    this.form.markAsDirty();
  }

  #buildDetailGroup(language: LanguageCode, seed?: PersistedTicketDetail): FormGroup {
    return this.#fb.group({
      language: [language, [Validators.required]],
      title: [
        seed?.title ?? '',
        [Validators.required, Validators.maxLength(255)],
      ],
    });
  }

  #resetDetailsTo(rows: PersistedTicketDetail[]) {
    while (this.details.length > 0) {
      this.details.removeAt(0);
    }
    const byLang = new Map<LanguageCode, PersistedTicketDetail>();
    for (const row of rows) {
      if ((LANGUAGE_CODES as readonly string[]).includes(row.language)) {
        byLang.set(row.language as LanguageCode, row);
      }
    }
    this.details.push(
      this.#buildDetailGroup(DEFAULT_LANGUAGE_CODE, byLang.get(DEFAULT_LANGUAGE_CODE)),
    );
    for (const code of LANGUAGE_CODES) {
      if (code === DEFAULT_LANGUAGE_CODE) continue;
      const seed = byLang.get(code);
      if (seed) {
        this.details.push(this.#buildDetailGroup(code, seed));
      }
    }
    this.detailsRevision.update((n) => n + 1);
  }

  #patchFromTicket(ticket: Ticket) {
    const persisted = ((ticket.details as PersistedTicketDetail[]) ?? []);
    this.form.reset({
      details: [],
      min_amount: ticket.min_amount,
      max_amount: ticket.max_amount,
      is_included: ticket.is_included,
    });
    this.#resetDetailsTo(persisted);
    this.form.markAsPristine();
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload = {
      product_id: this.productId(),
      min_amount: raw.min_amount ?? null,
      max_amount: raw.max_amount ?? null,
      is_included: raw.is_included,
      details: (raw.details as PersistedTicketDetail[]).map((row) => ({
        language: row.language as LanguageCode,
        title: row.title,
      })),
    };
    const parsed = StoreTicketSchema.safeParse(payload);
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const persisted = this.current();
    const call$ = persisted
      ? this.#service.update(persisted.id, parsed.data)
      : this.#service.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.current.set(response.data);
        this.form.markAsPristine();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            persisted ? 'catalog.tickets.updated' : 'catalog.tickets.created',
          ),
        });
        this.saved.emit(response.data);
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            persisted
              ? 'catalog.tickets.updateError'
              : 'catalog.tickets.createError',
          ),
        });
      },
    });
  }

  onDelete(event: Event) {
    const persisted = this.current();
    if (!persisted) {
      this.deleted.emit(-1);
      return;
    }
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant('catalog.tickets.deleteConfirmHeader'),
      message: this.#translate.instant('catalog.tickets.deleteConfirmMessage'),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.#service.delete(persisted.id).subscribe({
          next: () => {
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant('catalog.tickets.deleted'),
            });
            this.deleted.emit(persisted.id);
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant('catalog.tickets.deleteError'),
            });
          },
        });
      },
    });
  }

  reload() {
    this.saved.emit(this.current()!);
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
